// C:\Users\home\Desktop\coding_react\damoa-qr\src\pages\en\EXIFPage_en.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { parseNovelAIMetadata } from "../EXIF/novelaiMeta";
import "../EXIF/EXIFPage.css";

function EXIFPageEn() {
  const [darkMode, setDarkMode] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  // Left panel state
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewHeight, setPreviewHeight] = useState(400);

  // Tabs: initial "NovelAI"
  const [activeTab, setActiveTab] = useState("NovelAI");

  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  const EMPTY_TEXT = "No information found.";

  // NAI: field outputs
  const [naiPositiveDump, setNaiPositiveDump] = useState(EMPTY_TEXT);
  const [naiCharPositiveDump, setNaiCharPositiveDump] = useState(EMPTY_TEXT);
  const [naiNegativeDump, setNaiNegativeDump] = useState(EMPTY_TEXT);
  const [naiCharNegativeDump, setNaiCharNegativeDump] = useState(EMPTY_TEXT);
  const [naiModelDump, setNaiModelDump] = useState(EMPTY_TEXT);
  const [naiStealthDump, setNaiStealthDump] = useState(EMPTY_TEXT);

  // Current page canonical URL (CSR)
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI Image EXIF Extractor",
      description:
        "Free tool to extract EXIF and text metadata from PNG and WebP images. Supports NovelAI stealth PNG, prompts, seed, model, and more.",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "PNG, WebP, free, EXIF extractor, metadata, NovelAI, stealth PNG, prompt extractor, TooLoves",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // Theme color for browser/mobile nav bar
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const el = document.createElement("meta");
      el.name = "theme-color";
      el.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(el);
    }
  }, [darkMode]);

  // Mobile environment detection: UA + matchMedia(pointer, width)
  useEffect(() => {
    const getIsMobile = () => {
      if (typeof window === "undefined") return false;
      const ua = navigator.userAgent || navigator.vendor || "";
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      const supportsMatch = typeof window.matchMedia === "function";
      const coarse = supportsMatch ? window.matchMedia("(pointer: coarse)").matches : false;
      const maxWidth = supportsMatch ? window.matchMedia("(max-width: 820px)").matches : false;

      // UA-based OR touch-primary(coarse) AND reasonable width
      return uaMobile || (coarse && maxWidth);
    };

    const apply = () => setIsMobile(getIsMobile());
    apply();

    const onChange = () => apply();

    // Listeners
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    const mqlCoarse =
      typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)") : null;
    const mqlMax =
      typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 820px)") : null;

    mqlCoarse && mqlCoarse.addEventListener && mqlCoarse.addEventListener("change", onChange);
    mqlMax && mqlMax.addEventListener && mqlMax.addEventListener("change", onChange);

    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
      mqlCoarse && mqlCoarse.removeEventListener && mqlCoarse.removeEventListener("change", onChange);
      mqlMax && mqlMax.removeEventListener && mqlMax.removeEventListener("change", onChange);
    };
  }, []);

  // File upload (UI only)
  const addFiles = useCallback((list) => {
    const arr = Array.from(list || []);
    const imgs = arr.filter((f) => f.type.startsWith("image/"));
    const withId = imgs.map((f) => ({
      id: `${Date.now()}_${Math.random()}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setFiles((prev) => {
      const merged = [...prev, ...withId];
      return merged;
    });
    if (withId.length > 0) setSelectedId(withId[withId.length - 1].id);
  }, []);

  const onPickClick = useCallback(() => fileInputRef.current?.click(), []);
  const onFileChange = useCallback(
    (e) => {
      if (e.target.files?.length) addFiles(e.target.files);
      // Allow re-upload of the same file
      e.target.value = "";
    },
    [addFiles]
  );

  const onRemove = useCallback((id) => {
    setFiles((prev) => prev.filter((x) => x.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const onRemoveDuplicates = useCallback(() => {
    setFiles((prev) => {
      const seen = new Set();
      const deduped = [];
      for (let i = prev.length - 1; i >= 0; i--) {
        const name = prev[i].file.name;
        if (!seen.has(name)) {
          seen.add(name);
          deduped.unshift(prev[i]);
        }
      }
      return deduped;
    });
  }, []);

  const onReset = useCallback(() => {
    setFiles((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setSelectedId(null);
    setNaiPositiveDump(EMPTY_TEXT);
    setNaiCharPositiveDump(EMPTY_TEXT);
    setNaiNegativeDump(EMPTY_TEXT);
    setNaiCharNegativeDump(EMPTY_TEXT);
    setNaiModelDump(EMPTY_TEXT);
    setNaiStealthDump(EMPTY_TEXT);
  }, []);

  const selected = useMemo(
    () => files.find((f) => f.id === selectedId) || null,
    [files, selectedId]
  );

  // Safe JSON parser
  const tryParseJSON = useCallback((text) => {
    if (!text || typeof text !== "string") return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }, []);

  // Extract from stealth source
  const extractFromStealth = useCallback(
    (out) => {
      const raw = out?.stealth?.raw || "";
      const rootObj = tryParseJSON(raw);
      const commentStr =
        (rootObj && typeof rootObj.Comment === "string" && rootObj.Comment) || "";
      const commentObj = tryParseJSON(commentStr) || {};

      // Base/character prompts
      const pos = (commentObj.prompt || "").trim();
      const charPos = (commentObj?.v4_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // Negative prompt
      const neg =
        (commentObj?.v4_negative_prompt?.caption?.base_caption || "").trim() ||
        (commentObj.uc || "").trim();

      const charNeg = (commentObj?.v4_negative_prompt?.caption?.char_captions || [])
        .map((c) => (c?.char_caption || "").trim())
        .filter(Boolean)
        .join("\n")
        .trim();

      // Model/settings
      const steps = commentObj?.steps;
      const sampler = commentObj?.sampler;
      const noise = commentObj?.noise_schedule;
      const seed = commentObj?.seed;
      const width = commentObj?.width;
      const height = commentObj?.height;
      const scale = commentObj?.scale;
      const cfgRescale = commentObj?.cfg_rescale;

      // Source priority
      const source =
        rootObj?.Source ||
        out?.pngTexts?.texts?.Source ||
        out?.webpMeta?.customTexts?.Source ||
        "";

      const modelLines = [];
      if (steps != null) modelLines.push(`Steps: ${steps}`);
      if (sampler != null) modelLines.push(`Sampler: ${sampler}`);
      if (noise != null) modelLines.push(`Noise Schedule: ${noise}`);
      if (seed != null) modelLines.push(`Seed: ${seed}`);
      if (width != null && height != null) modelLines.push(`Size: ${width} x ${height}`);
      if (scale != null) modelLines.push(`Scale: ${scale}`);
      if (cfgRescale != null) modelLines.push(`CFG Rescale: ${cfgRescale}`);
      if (source) modelLines.push(`Source: ${source}`);

      const prettyStealth = rootObj ? JSON.stringify(rootObj, null, 2) : raw || "";

      return {
        pos,
        charPos,
        neg,
        charNeg,
        modelDump: modelLines.join("\n"),
        stealthPretty: prettyStealth,
      };
    },
    [tryParseJSON]
  );

  // Drag and drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      e.preventDefault();
      el.classList.add("dragover");
    };
    const onDragLeave = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
    };
    const onDrop = (e) => {
      e.preventDefault();
      el.classList.remove("dragover");
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  // novelaiMeta.js integration
  useEffect(() => {
    let cancelled = false;
    async function run() {
      // Reset
      setNaiPositiveDump(EMPTY_TEXT);
      setNaiCharPositiveDump(EMPTY_TEXT);
      setNaiNegativeDump(EMPTY_TEXT);
      setNaiCharNegativeDump(EMPTY_TEXT);
      setNaiModelDump(EMPTY_TEXT);
      setNaiStealthDump(EMPTY_TEXT);

      if (!selected?.file) return;

      try {
        const file = selected.file;
        const ab = await file.arrayBuffer();
        const out = await parseNovelAIMetadata({
          arrayBuffer: ab,
          mime: file.type,
          imageSource: file, // for stealth_pngcomp
        });

        // 1st: Extract directly from stealth source
        const fromStealth = extractFromStealth(out);

        // 2nd: Fallback from parser output
        const fallbackPos = (out?.result?.sampler?.prompts || []).join("\n").trim();
        const fallbackNeg = (out?.result?.sampler?.negative_prompts || [])
          .join("\n")
          .trim();

        const pos = fromStealth.pos || fallbackPos;
        const neg = fromStealth.neg || fallbackNeg;

        if (!cancelled) {
          setNaiPositiveDump(pos || EMPTY_TEXT);
          setNaiCharPositiveDump(fromStealth.charPos || EMPTY_TEXT);
          setNaiNegativeDump(neg || EMPTY_TEXT);
          setNaiCharNegativeDump(fromStealth.charNeg || EMPTY_TEXT);
          setNaiModelDump(fromStealth.modelDump || EMPTY_TEXT);
          setNaiStealthDump(fromStealth.stealthPretty || EMPTY_TEXT);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = `Parsing error: ${e?.message || String(e)}`;
          setNaiPositiveDump(msg);
          setNaiNegativeDump(EMPTY_TEXT);
          setNaiCharPositiveDump(EMPTY_TEXT);
          setNaiCharNegativeDump(EMPTY_TEXT);
          setNaiModelDump(EMPTY_TEXT);
          setNaiStealthDump(EMPTY_TEXT);
        }
      }
    }
    run();
    return () => {
      let _ = cancelled; // silence eslint
      cancelled = true;
    };
  }, [selected, extractFromStealth]);

  // Common copy helper
  const copyText = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch {}
  }, []);

  const isEmpty = (v) => !v || v.trim() === "" || v === EMPTY_TEXT;

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>AI Image EXIF Extractor | TooLoves</title>
        <meta
          name="description"
          content="Free tool to extract EXIF and text metadata from PNG and WebP images. Supports NovelAI stealth PNG, prompts, seed, model, and more."
        />
        <meta
          name="keywords"
          content="PNG, WebP, free, EXIF extractor, metadata, image metadata, NovelAI, stealth PNG, prompt extractor, TooLoves"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Initial theme-color; synced at runtime via useEffect */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="AI Image EXIF Extractor | TooLoves" />
        <meta
          property="og:description"
          content="Extract EXIF and text metadata from PNG/WebP images for free."
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-exif.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Image EXIF Extractor | TooLoves" />
        <meta
          name="twitter:description"
          content="Extract EXIF and text metadata from PNG/WebP images for free."
        />
        <meta name="twitter:image" content="/og-exif.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/en" className="brand-link" aria-label="Go to TooLoves home">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode((v) => !v)}
            aria-label="Toggle theme"
          >
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>
        </div>
      </header>

      <main className="container exif-container">
        {isMobile ? (
          // Mobile notice block
          <section className="generator-card" role="region" aria-label="Mobile support notice">
            <div className="metadata-section">
              <h3 className="section-title">Mobile Support</h3>
              <p className="hint">This feature is not supported on mobile.</p>
              <div className="terms-actions">
                <Link to="/en" className="btn" aria-label="Go back to Home">
                  Back to Home
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* Left panel */}
            <section className="generator-card panel-left">
              <div className="upload-header">
                <h4 className="section-title">Upload Images</h4>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={onFileChange}
                />
                <button
                  type="button"
                  onClick={onPickClick}
                  aria-label="Select image files"
                  className="btn btn-primary"
                >
                  Choose Files
                </button>
              </div>

              <div ref={dropRef} id="drop-zone">
                Drag and drop images here, or use the Choose Files button
              </div>

              <div className="preview-header">
                <h2 className="section-title small">Image Preview</h2>
                <div className="preview-size-control">
                  <input
                    type="range"
                    min={100}
                    max={1200}
                    value={previewHeight}
                    onChange={(e) => setPreviewHeight(parseInt(e.target.value, 10))}
                  />
                  <span id="preview-size-value">{previewHeight}px</span>
                </div>
              </div>

              <div className="qr-wrap">
                {selected?.url ? (
                  <img
                    className="preview-img"
                    src={selected.url}
                    alt="Image preview"
                    height={previewHeight}
                  />
                ) : (
                  <p className="placeholder">No image selected</p>
                )}
              </div>

              <div className="actions">
                <button onClick={onRemoveDuplicates}>Remove Duplicates</button>
                <button onClick={onReset}>Reset</button>
              </div>

              <h2 className="section-title small list-title">Image List</h2>
              <ul id="image-list">
                {files.map((f) => (
                  <li
                    key={f.id}
                    className={`file-row ${f.id === selectedId ? "selected" : ""}`}
                    onClick={() => setSelectedId(f.id)}
                  >
                    <span className="file-name">{f.file.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(f.id);
                      }}
                      className="btn btn-secondary"
                      aria-label={`Remove ${f.file.name}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </section>

            {/* Right panel */}
            <section className="generator-card panel-right">
              {/* Tabs */}
              <ul className="tabs">
                {["NovelAI"].map((label) => {
                  const key = label;
                  const active = activeTab === key;
                  return (
                    <li
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={active ? "active" : ""}
                    >
                      {label}
                    </li>
                  );
                })}
              </ul>

              {/* NovelAI tab */}
              <div className={`tab-content ${activeTab === "NovelAI" ? "active" : ""}`}>
                <div className="metadata-section">
                  {/* Positive Prompt */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-positive" className="label-strong">
                        Positive Prompt
                      </label>
                      <button
                        onClick={() => copyText(naiPositiveDump)}
                        aria-label="Copy positive prompt"
                        disabled={isEmpty(naiPositiveDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiPositiveDump}
                    </div>
                  </div>

                  {/* Character Positive Prompt */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-positive" className="label-strong">
                        Character Positive Prompt
                      </label>
                      <button
                        onClick={() => copyText(naiCharPositiveDump)}
                        aria-label="Copy character positive prompt"
                        disabled={isEmpty(naiCharPositiveDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-char-positive"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharPositiveDump}
                    </div>
                  </div>

                  {/* Negative Prompt */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-negative" className="label-strong">
                        Negative Prompt
                      </label>
                      <button
                        onClick={() => copyText(naiNegativeDump)}
                        aria-label="Copy negative prompt"
                        disabled={isEmpty(naiNegativeDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiNegativeDump}
                    </div>
                  </div>

                  {/* Character Negative Prompt */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-char-negative" className="label-strong">
                        Character Negative Prompt
                      </label>
                      <button
                        onClick={() => copyText(naiCharNegativeDump)}
                        aria-label="Copy character negative prompt"
                        disabled={isEmpty(naiCharNegativeDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-char-negative"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiCharNegativeDump}
                    </div>
                  </div>

                  {/* NAI Model & Settings */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-model" className="label-strong">
                        NAI Model & Settings
                      </label>
                      <button
                        onClick={() => copyText(naiModelDump)}
                        aria-label="Copy NAI model and settings"
                        disabled={isEmpty(naiModelDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-model"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiModelDump}
                    </div>
                  </div>

                  {/* Stealth PNG Metadata */}
                  <div className="block">
                    <div className="label-row">
                      <label htmlFor="nai-stealth" className="label-strong">
                        Stealth PNG Metadata
                      </label>
                      <button
                        onClick={() => copyText(naiStealthDump)}
                        aria-label="Copy stealth PNG metadata"
                        disabled={isEmpty(naiStealthDump)}
                        className="btn btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div
                      id="nai-stealth"
                      role="textbox"
                      aria-readonly="true"
                      tabIndex={0}
                      className="mono-box"
                    >
                      {naiStealthDump}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="Legal links">
          <Link to="/en/terms" className="footer-link">
            Terms of Service
          </Link>
          <span className="footer-sep" aria-hidden="true">
            ·
          </span>
          <Link to="/en/privacy" className="footer-link">
            Privacy Policy
          </Link>
        </nav>
      </footer>
    </div>
  );
}

export default EXIFPageEn;